import { alpha, Box, Modal, Radio, Stack, Typography, useColorScheme } from "@mui/material";
import { Constants } from "../../utils/constants";
import { DefaultButton, SuccessButton } from "..";
import { useState } from "react";
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

export default function UserTransferOwnerModal({
    open, onClose, onFinish, user, users
}) {

    const { mode } = useColorScheme();
    const currentMode = mode || "system";
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 360,
        maxWidth: 450,
        bgcolor: 'background.paper',
        border: `1px solid ${Constants.Colors[currentMode].border}`,
        borderRadius: "8px",
        boxShadow: 24,
        p: "1.5rem",
    };
    const [selectedUser, setSelectedUser] = useState(null);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-user-remove-title"
            aria-describedby="modal-user-remove-description"
        >
            <Box sx={style}>
                <Typography
                    sx={{
                        fontWeight: Constants.Styles.fontWeight.semiBold,
                        fontSize: Constants.Styles.fontSize.lg,
                        lineHeight: Constants.Styles.lineHeight.lg,
                    }}
                >
                    Transfer Ownership
                </Typography>
                <Typography
                    sx={{
                        fontWeight: Constants.Styles.fontWeight.normal,
                        fontSize: Constants.Styles.fontSize.sm,
                        lineHeight: Constants.Styles.lineHeight.sm,
                        color: Constants.Colors[currentMode].mutedForeground,
                        mt: "0.25rem",
                    }}
                >
                    Select a team member to transfer ownership to
                </Typography>
                <Box
                    sx={{
                        backgroundColor: alpha(Constants.Colors[currentMode].warning, 0.1),
                        border: `1px solid ${Constants.Colors[currentMode].warning}`,
                        borderRadius: `calc(${Constants.Styles.radius.base} - 2px)`,
                        paddingBlock: `calc(${Constants.Styles.spacing} * 1.5)`,
                        paddingInline: `calc(${Constants.Styles.spacing} * 2)`,
                        marginBlock: `calc(${Constants.Styles.spacing} * 4)`,
                    }}
                >
                    <Stack
                        direction={"row"}
                        alignItems={"start"}
                        justifyContent={"start"}
                        spacing={1}
                    >
                        <WarningAmberOutlinedIcon sx={{
                            color: Constants.Colors[currentMode].warning,
                            fontSize: Constants.Styles.fontSize["2xl"],
                        }} />
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: Constants.Styles.fontWeight.medium,
                                    fontSize: Constants.Styles.fontSize.sm,
                                    lineHeight: Constants.Styles.lineHeight.sm,
                                    color: Constants.Colors[currentMode].warning,
                                }}
                            >
                                This action is irreversible
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: Constants.Styles.fontWeight.normal,
                                    fontSize: Constants.Styles.fontSize.sm,
                                    lineHeight: Constants.Styles.lineHeight.sm,
                                    color: alpha(Constants.Colors[currentMode].warning, 0.8),
                                    mt: "0.25rem",
                                }}
                            >
                                You will become an Admin after transferring ownership.
                            </Typography>
                        </Box>
                    </Stack>

                </Box>
                <Typography
                    sx={{
                        fontWeight: Constants.Styles.fontWeight.semiBold,
                        fontSize: Constants.Styles.fontSize.sm,
                        lineHeight: Constants.Styles.lineHeight.sm,
                        marginBlock: "1.0rem",
                    }}
                >
                    Select new owner
                </Typography>
                <Stack
                    spacing={"0.75rem"}
                    sx={{
                        maxHeight: { xs: 300, sm: 300, md: 500, },
                        overflowY: "scroll",
                    }}
                >
                    {users?.map((u, index) => {
                        const selected = selectedUser?.user_id === u?.user_id;
                        return (
                            <Stack
                                key={index}
                                direction={"column"}
                                spacing={"1.125rem"}
                                sx={{
                                    padding: "0.75rem 0.25rem",
                                    backgroundColor: selected ? alpha(Constants.Colors[currentMode].accent, 0.05) : alpha(Constants.Colors[currentMode].secondary, 0.3),
                                    borderRadius: `calc(${Constants.Styles.radius.base} * 1)`,
                                    border: `1px solid ${selected ? Constants.Colors[currentMode].accent : Constants.Colors[currentMode].border}`,
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    setSelectedUser(u);
                                }}
                            >
                                <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"start"}
                                    spacing={0}
                                >
                                    <Radio
                                        checked={selectedUser?.user_id === u?.user_id}
                                        onChange={() => {
                                            setSelectedUser(u);
                                        }}
                                        sx={{
                                            '& .MuiSvgIcon-root': {
                                                fontSize: 20,
                                            },
                                            color: selected ? alpha(Constants.Colors[currentMode].accent, 0.7) : alpha(Constants.Colors[currentMode].foreground, 0.7),
                                            '&.Mui-checked': {
                                                color: selected ? Constants.Colors[currentMode].accent : alpha(Constants.Colors[currentMode].foreground, 0.7),
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontWeight: Constants.Styles.fontWeight.medium,
                                            fontSize: Constants.Styles.fontSize.sm,
                                            lineHeight: Constants.Styles.lineHeight.sm,
                                        }}
                                    >{u.user_id}</Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: Constants.Styles.fontWeight.normal,
                                            fontSize: Constants.Styles.fontSize.xs,
                                            lineHeight: Constants.Styles.lineHeight.xs,
                                            color: Constants.Colors[currentMode].mutedForeground,
                                            pl: 1,
                                        }}
                                    >{u.role}</Typography>
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"end"}
                    sx={{
                        mt: "1.5rem",
                    }}
                    spacing={2}
                >
                    <DefaultButton
                        text={"Cancel"}
                        size="medium"
                        onClick={() => {
                            onClose();
                        }}
                        sx={{
                            width: 60,
                        }}
                    />
                    <SuccessButton
                        text={"Transfer Ownership"}
                        size="medium"
                        onClick={() => {
                            onFinish(selectedUser);
                        }}
                        sx={{
                            width: 160,
                        }}
                    />
                </Stack>
            </Box>
        </Modal>
    );
}